/**
 * AuthImage — Image component that fetches with auth headers.
 *
 * Browser <img> tags can't set Authorization headers, so we fetch
 * the image as a blob and create an object URL.
 */

import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { ensureValidToken, getAccessToken } from "../lib/api";
import styles from "./ChatView.module.css";

const BASE_URL = "/api/v1";

interface AuthImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  onClick?: (blobUrl: string) => void;
  /** AES-256-GCM key for client-encrypted files (base64url) */
  encryptionKey?: string;
  /** AES-256-GCM IV for client-encrypted files (base64url) */
  encryptionIv?: string;
  /** Original MIME type (encrypted files are stored as application/octet-stream) */
  originalMime?: string;
}

export function AuthImage({ src, alt, className, onClick, encryptionKey, encryptionIv, originalMime }: AuthImageProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;

    async function loadImage() {
      try {
        // Ensure valid token before fetching
        await ensureValidToken();
        const token = getAccessToken();

        // Build full URL — strip /api/v1 prefix if present (we add it ourselves)
        const path = src.startsWith("/api/v1")
          ? src.replace("/api/v1", "")
          : src;
        const fullUrl = `${BASE_URL}${path}`;

        const res = await fetch(fullUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const arrayBuffer = await res.arrayBuffer();

        // Decrypt if this is a client-encrypted file
        let finalBuffer = arrayBuffer;
        if (encryptionKey && encryptionIv) {
          const { decryptFile } = await import("../lib/file-crypto");
          finalBuffer = await decryptFile(arrayBuffer, encryptionKey, encryptionIv);
        }

        const contentType = originalMime ?? res.headers.get("content-type") ?? "image/jpeg";
        const blob = new Blob([finalBuffer], { type: contentType });
        url = URL.createObjectURL(blob);

        if (!cancelled) setBlobUrl(url);
      } catch (err) {
        console.error("AuthImage load error:", src, err);
        if (!cancelled) setError(true);
      }
    }

    loadImage();

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src, retryCount]);

  if (error) {
    return (
      <div className={styles.imageFallback} onClick={() => { setError(false); setRetryCount(c => c + 1); }}>
        <span>🖼️ Bild konnte nicht geladen werden — tippen zum Neuladen</span>
      </div>
    );
  }

  if (!blobUrl) {
    return (
      <div className={styles.imagePlaceholder}>
        <span className={styles.imageLoading}><Loader2 size={24} className="spin" /></span>
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      onClick={() => blobUrl && onClick?.(blobUrl)}
    />
  );
}
